/**
 * Gift With Purchase — reconciliation module.
 *
 * The single authority for adding/removing the gift line item. All cart
 * writes are serialized through one promise queue; reconciliation never
 * triggers itself (we do not publish cartUpdate for our own mutations).
 *
 * State model (see spec):
 *   below threshold        -> no gift line, `_gwp_declined` cleared
 *   qualified, active      -> gift line present, qty 1
 *   qualified, declined    -> gift removed, `_gwp_declined` = "true" (placeholder row is Liquid-rendered)
 */
(() => {
  const configEl = document.getElementById('gwp-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config.variantId) return;

  const VARIANT_ID = String(config.variantId);
  const THRESHOLD = Number(config.threshold) || 0;
  // A zero/negative threshold would qualify every cart — treat as misconfigured.
  if (THRESHOLD <= 0) return;

  // Set true after a 422 (or when the page loaded with no stock) so we never
  // hammer /cart/add with a dead variant. NOT permanent: inventory can be
  // replenished while a tab stays open, so before skipping an add we re-check
  // the product's live availability (throttled) and self-heal on restock.
  let giftUnavailable = !config.available;
  let lastAvailabilityCheck = 0;
  const AVAILABILITY_RECHECK_MS = 30000;

  // All cart traffic (reconciles and button actions) flows through this queue.
  let queue = Promise.resolve();
  const enqueue = (job) => {
    queue = queue.then(job).catch((e) => console.warn('[gwp]', e));
    return queue;
  };

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const routeFor = (name, fallback) =>
    (window.routes && window.routes[name]) || fallback;

  /* ---------------------------------------------------------------- */
  /* Section rendering                                                 */
  /* ---------------------------------------------------------------- */

  const onCartPage = () => Boolean(document.getElementById('main-cart-items'));

  // Mirrors CartItems/CartDrawerItems getSectionsToRender().
  const sectionsToRender = () => {
    if (onCartPage()) {
      const items = document.getElementById('main-cart-items');
      const footer = document.getElementById('main-cart-footer');
      const sections = [
        { id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];
      if (footer) {
        sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
      }
      return sections;
    }
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  };

  const sectionInnerHTML = (html, selector) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector) || doc.querySelector('.shopify-section');
    return el ? el.innerHTML : null;
  };

  const renderSections = (state) => {
    if (!state || !state.sections) return;
    sectionsToRender().forEach(({ id, section, selector }) => {
      const html = state.sections[section];
      if (!html) return;
      const root = document.getElementById(id);
      if (!root) return;
      const target = root.querySelector(selector) || root;
      const replacement = sectionInnerHTML(html, selector);
      if (replacement !== null) target.innerHTML = replacement;
    });

    const itemCount = typeof state.item_count === 'number' ? state.item_count : null;
    if (itemCount !== null) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', itemCount === 0);
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', itemCount === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
    }

    if (giftUnavailable) hidePromo();
  };

  const hidePromo = () => {
    document.querySelectorAll('.gwp-promo').forEach((el) => {
      el.style.display = 'none';
    });
  };

  const showPromo = () => {
    document.querySelectorAll('.gwp-promo').forEach((el) => {
      el.style.display = '';
    });
  };

  // Live availability check against the product JSON endpoint. Returns true
  // (and clears the unavailable memo) when the gift variant is back in stock.
  const recheckAvailability = async () => {
    if (!config.handle) return false;
    const now = Date.now();
    if (now - lastAvailabilityCheck < AVAILABILITY_RECHECK_MS) return false;
    lastAvailabilityCheck = now;
    try {
      const res = await fetch(`/products/${config.handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const product = await res.json();
      const variant = (product.variants || []).find((v) => String(v.id) === VARIANT_ID);
      if (variant && variant.available) {
        giftUnavailable = false;
        showPromo();
        return true;
      }
    } catch (e) {
      /* network hiccup: stay unavailable, retry after cooldown */
    }
    return false;
  };

  /* ---------------------------------------------------------------- */
  /* Cart API                                                          */
  /* ---------------------------------------------------------------- */

  const sectionIds = () => sectionsToRender().map((s) => s.section);

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  };

  const addGift = async () => {
    const res = await fetch(`${routeFor('cart_add_url', '/cart/add')}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        items: [{ id: Number(VARIANT_ID), quantity: 1 }],
        sections: sectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    if (res.status === 422) {
      // Variant unpurchasable/out of stock: the customer never asked for the
      // hat, so this is not an error state they should see (spec §7.4).
      giftUnavailable = true;
      hidePromo();
      return null;
    }
    if (!res.ok) return null;
    // A successful add proves the variant is purchasable.
    giftUnavailable = false;
    const state = await res.json();
    // /cart/add.js returns the added line, not the cart; item_count omitted on purpose.
    renderSections(state);
    return state;
  };

  // Removes the gift and writes the declined attribute in one round trip.
  const updateCart = async ({ updates, attributes }) => {
    const body = { sections: sectionIds(), sections_url: window.location.pathname };
    if (updates) body.updates = updates;
    if (attributes) body.attributes = attributes;
    const res = await fetch(`${routeFor('cart_update_url', '/cart/update')}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const state = await res.json();
    renderSections(state);
    return state;
  };

  const forceQuantityOne = async () => {
    const res = await fetch(`${routeFor('cart_change_url', '/cart/change')}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        id: VARIANT_ID,
        quantity: 1,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) return null;
    const state = await res.json();
    renderSections(state);
    return state;
  };

  /* ---------------------------------------------------------------- */
  /* Reconciliation                                                    */
  /* ---------------------------------------------------------------- */

  const qualifyingSubtotal = (cart) =>
    cart.items.reduce(
      (sum, item) =>
        String(item.variant_id) === VARIANT_ID ? sum : sum + item.original_line_price,
      0
    );

  const giftLines = (cart) =>
    cart.items.filter((item) => String(item.variant_id) === VARIANT_ID);

  const reconcile = async () => {
    const cart = await fetchCart();
    const present = giftLines(cart);
    const declined = cart.attributes && cart.attributes._gwp_declined === 'true';
    const qualified = qualifyingSubtotal(cart) >= THRESHOLD;

    let mutated = false;

    if (!config.enabled) {
      // Kill switch: strip the gift on the next cart interaction.
      if (present.length) {
        await updateCart({ updates: { [VARIANT_ID]: 0 }, attributes: { _gwp_declined: '' } });
        mutated = true;
      }
    } else if (!qualified) {
      // Dropping below the threshold resets the declined flag: re-crossing
      // is a fresh event each time (spec §5.3).
      if (present.length) {
        await updateCart({ updates: { [VARIANT_ID]: 0 }, attributes: { _gwp_declined: '' } });
        mutated = true;
      } else if (declined) {
        await updateCart({ attributes: { _gwp_declined: '' } });
        mutated = true;
      }
    } else if (declined && present.length) {
      // Stale state (e.g. another tab re-added): declined wins.
      await updateCart({ updates: { [VARIANT_ID]: 0 } });
      mutated = true;
    } else if (!declined && !present.length) {
      // Sold out: never add, but existing carts keep their gift (§8).
      // The memo self-heals: stock may have been replenished since it was set.
      if (giftUnavailable) await recheckAvailability();
      if (!giftUnavailable) {
        await addGift();
        mutated = true;
      }
    } else if (present.length) {
      const totalQty = present.reduce((sum, item) => sum + item.quantity, 0);
      if (totalQty !== 1) {
        await forceQuantityOne();
        mutated = true;
      }
    }

    // Nothing to change server-side — but the DOM can still be stale: another
    // tab may have mutated the cart, or a slower theme request may have painted
    // over one of our renders. Repaint from the server if the gift row on
    // screen contradicts the actual cart.
    if (!mutated) await syncDomIfStale({ present: present.length > 0, declined, qualified, cart });
  };

  const syncDomIfStale = async ({ present, declined, qualified, cart }) => {
    const context = document.getElementById('CartDrawer') || document.getElementById('main-cart-items');
    if (!context) return;

    const expectGift = present;
    const expectPlaceholder =
      !present && config.enabled && !giftUnavailable && qualified && declined;
    const hasGift = !!context.querySelector('.gwp-item:not(.gwp-item--declined)');
    const hasPlaceholder = !!context.querySelector('.gwp-item--declined');
    if (hasGift === expectGift && hasPlaceholder === expectPlaceholder) return;

    const res = await fetch(`${window.location.pathname}?sections=${sectionIds().join(',')}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    const sections = await res.json();
    renderSections({ sections, item_count: cart.item_count });
  };

  /* ---------------------------------------------------------------- */
  /* Customer actions (gift row buttons)                               */
  /* ---------------------------------------------------------------- */

  const decline = () =>
    updateCart({ updates: { [VARIANT_ID]: 0 }, attributes: { _gwp_declined: 'true' } });

  const accept = async () => {
    const state = await addGift();
    if (state) await updateCart({ attributes: { _gwp_declined: '' } });
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-gwp-remove]')) {
      event.preventDefault();
      enqueue(decline);
    } else if (event.target.closest('[data-gwp-add]')) {
      event.preventDefault();
      enqueue(accept);
    } else if (event.target.closest('#cart-icon-bubble')) {
      // Drawer open: the cart cookie is shared across tabs and can be stale.
      enqueue(reconcile);
    }
  });

  /* ---------------------------------------------------------------- */
  /* Triggers                                                          */
  /* ---------------------------------------------------------------- */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'gwp') return;
      enqueue(reconcile);
    });
  }

  // Safari/iOS restore pages from the back-forward cache (e.g. returning from
  // checkout) with whatever DOM they left with — reconcile against the live cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) enqueue(reconcile);
  });

  const start = () => enqueue(reconcile);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
